import { useState } from 'react';
import { Button } from '@components/Button';
import { IconButton } from '@components/IconButton';
import { IconClose } from '@components/icons';
import { MenuSelect } from '@components/MenuSelect';
import styles from './ChangeBusinessTypeModal.module.scss';

export interface ChangeBusinessTypeModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** The currently assigned business type slug */
  currentSlug: string;
  /** All available business types as { value: slug, label: name } */
  availableTypes: Array<{ value: string; label: string }>;
  /** Called with the new slug when user confirms. Returns a promise for loading state. */
  onSave: (slug: string) => Promise<void>;
}

const ChangeBusinessTypeForm = ({
  onClose,
  currentSlug,
  availableTypes,
  onSave,
}: Pick<
  ChangeBusinessTypeModalProps,
  'onClose' | 'currentSlug' | 'availableTypes' | 'onSave'
>) => {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(currentSlug);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSaveDisabled = !selectedSlug || selectedSlug === currentSlug || isSaving;

  const handleSave = async () => {
    if (!selectedSlug || isSaveDisabled) return;
    setIsSaving(true);
    setError(null);
    try {
      await onSave(selectedSlug);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers */}
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-business-type-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          e.stopPropagation();
        }}
      >
        <div className={styles.header}>
          <h2 id="change-business-type-title" className={styles.title}>
            Change Business Type
          </h2>
          <IconButton size="small" aria-label="Close modal" onClick={onClose} className={styles.closeButton}>
            <IconClose size={18} />
          </IconButton>
        </div>

        <div className={styles.content}>
          <MenuSelect
            items={availableTypes}
            value={selectedSlug}
            onChange={setSelectedSlug}
            filterable
            filterPlaceholder="Search business types..."
            maxHeight={280}
          />
          {error && <p className={styles.error}>{error}</p>}
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" size="small" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" size="small" onClick={handleSave} disabled={isSaveDisabled}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ChangeBusinessTypeModal = ({
  isOpen,
  onClose,
  currentSlug,
  availableTypes,
  onSave,
}: ChangeBusinessTypeModalProps) => {
  if (!isOpen) return null;
  return (
    <ChangeBusinessTypeForm
      onClose={onClose}
      currentSlug={currentSlug}
      availableTypes={availableTypes}
      onSave={onSave}
    />
  );
};
